import { getSessionFromStorage } from '@consigli/oauth';
import { ConflictType } from '@consigli/types';
import { mapToSnakeCase } from '@consigli/utils';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { FeatureFlag, useFeatureFlag } from '@/feature-flags';
import { type Message, type MessageSource, type MessageStep } from '@/util/interfaces';

import { type AddContextSelection } from './types';
import { useAgentChatAttachments } from './use-agent-chat-attachments';
import { parseStreamSources } from './utils';

interface AgentChatSession {
  // Null until the first question of a chat is asked, since a conversation the
  // user never starts should not claim an id.
  conversationId: string | null;
  isResumedChat: boolean;
  isStreaming: boolean;
  messages: Message[];
}

// Storage is not a trusted input: an entry outlives the build that wrote it,
// and can be hand-edited or left behind by an older version. Parsing it rather
// than casting is what keeps a resumed message the same shape as a live one -
// a cast let a serialised date come back as a string wearing a Date's type,
// which would have failed only after a reload.
const storedMessageSchema = z.object({
  id: z.string(),
  body: z.string(),
  // Not z.coerce.date(): an ISO string is what survives JSON, what every other
  // serialised timestamp in libs/types is typed as, and what the shared date
  // helpers already take. Live and stored messages therefore hold the same
  // thing, so there is no revival step left for a later change to forget.
  createdAt: z.string(),
  authorId: z.string(),
  // content is optional because the stream's v2 sources are ids alone; a
  // transcript stored before that change still carries the v1 passage text.
  // Both ids are optional because a source is one kind or the other, and a
  // required blobId would make every stored finding source fail to rehydrate.
  sources: z
    .array(
      z.object({
        blobId: z.string().optional(),
        findingId: z.number().optional(),
        // Kept through a reload, or a restored milestone counts as a plain
        // finding. A value this build does not know drops to absent rather
        // than failing the message, which would cost the whole turn.
        conflictType: z.nativeEnum(ConflictType).optional().catch(undefined),
        content: z.string().optional(),
      }),
    )
    .optional(),
  steps: z
    .array(
      z.object({
        key: z.string(),
        status: z.enum(['active', 'done']),
        detail: z.string().optional(),
      }),
    )
    .optional(),
  thinkingSeconds: z.number().optional(),
});

// Skips a message that fails validation instead of discarding the transcript:
// one corrupt entry should not cost the user every other turn. The chat API
// takes the same line with its own stored history.
const storedMessagesSchema = z.array(z.unknown()).transform((items) =>
  items.flatMap((item) => {
    const parsed = storedMessageSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

/**
 * What actually goes into storage: the id and the transcript together, never
 * the runtime flags. Kept as one entry rather than two keys so the two cannot
 * drift — a pair of setItem calls is not atomic, and a quota failure on the
 * second would leave an id pointing at a transcript that was never saved.
 */
const storedSessionSchema = z.object({
  conversationId: z.string().uuid(),
  messages: storedMessagesSchema,
  updatedAt: z.string(),
});

type StoredSession = z.infer<typeof storedSessionSchema>;

interface AgentChatSessionContextValue {
  sendMessage: (message: Message, language: string) => Promise<void>;
  session: AgentChatSession;
}

interface AgentChatSessionProviderProps {
  packageId: string;
  projectId: number;
}

// An answer types out, accelerating as it goes: 2 characters a frame for the
// first 50, two more per 50 after that, up to 10 which the rest of the answer
// keeps. Roughly twice the speed of the delay-based version it replaces —
// about 4 seconds for 2000 characters rather than 8.
//
// Paced by frame rather than by a per-character delay because browsers clamp a
// nested setTimeout to a 4ms minimum: every delay below that behaved
// identically, so shortening it could not speed the typing up. Frames are not
// clamped, are synced to paint so no render is wasted, and put the speed dial
// on characters-per-frame, which has no floor. Raise
// ANIMATION_MAX_CHARS_PER_FRAME to go faster.
const ANIMATION_START_CHARS_PER_FRAME = 2;
const ANIMATION_CHARS_PER_FRAME_INCREMENT = 2;
const ANIMATION_MAX_CHARS_PER_FRAME = 10;
const ANIMATION_STEP_CHARS = 50;
// Once the stream has finished there is no reason to keep pace with it, so
// whatever is still queued is revealed at double speed.
const DRAIN_SPEED_MULTIPLIER = 2;

export const charactersPerFrame = (position: number) =>
  Math.min(
    ANIMATION_MAX_CHARS_PER_FRAME,
    ANIMATION_START_CHARS_PER_FRAME +
      ANIMATION_CHARS_PER_FRAME_INCREMENT * Math.floor(position / ANIMATION_STEP_CHARS),
  );

const nextFrame = () =>
  new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 16);
  });

const CONNECT_TIMEOUT_MS = 30_000;

class QueryError extends Error {
  constructor(readonly code?: string) {
    super(code ?? 'Query failed');
  }
}

// The server only ever sends a coarse code, never the underlying cause; unknown
// codes fall back to the generic failure message.
const QUERY_FAILURE_KEYS: Record<string, string> = {
  SERVICE_UNAVAILABLE: 'prop-chat.query-unavailable',
  PACKAGE_NOT_FOUND: 'prop-chat.query-not-found',
};

const getStorageKey = (packageId: string) => `propchat-${packageId}`;

const storeSession = (packageId: string, conversationId: string, messages: Message[]) => {
  try {
    // updatedAt is recorded but not yet acted on. The server drops a
    // conversation an hour after its last turn, so honouring that client-side
    // is a decision still open — having the stamp already stored means taking
    // it does not need a second change of shape.
    const stored: StoredSession = {
      conversationId,
      messages,
      updatedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(getStorageKey(packageId), JSON.stringify(stored));
  } catch (error) {
    // Citations carry a lot of text, so a long conversation can exceed the
    // storage quota. Losing the ability to resume is survivable; letting this
    // throw is not — it escapes sendMessage through the finally, leaving
    // isStreaming stuck on and the composer disabled until a reload.
    console.warn('Could not persist the Agent Chat history:', error);
  }
};

const readStoredSession = (packageId: string): AgentChatSession => {
  const stored = sessionStorage.getItem(getStorageKey(packageId));

  if (stored !== null) {
    try {
      const parsed: unknown = JSON.parse(stored);
      const session = storedSessionSchema.safeParse(parsed);

      if (session.success) {
        return {
          conversationId: session.data.conversationId,
          isResumedChat: true,
          isStreaming: false,
          messages: session.data.messages,
        };
      }

      // A bare array is the shape stored before a conversation had an id. Those
      // turns were sent without one, so the server holds no history for them
      // either — nothing is lost by letting the next question mint a fresh id.
      // An object fails this too, so anything unrecognised lands on a fresh chat.
      const legacyMessages = storedMessagesSchema.safeParse(parsed);

      if (legacyMessages.success) {
        return {
          conversationId: null,
          isResumedChat: true,
          isStreaming: false,
          messages: legacyMessages.data,
        };
      }
    } catch {
      // Unparseable stored history falls through to a fresh chat.
    }
  }

  return { conversationId: null, isResumedChat: false, isStreaming: false, messages: [] };
};

const AgentChatSessionContext = createContext<AgentChatSessionContextValue | null>(null);

/**
 * Owns the Agent Chat conversation and its in-flight answer stream.
 *
 * This deliberately sits above the workspace pages rather than inside the
 * drawer: every page renders its own WorkspaceLayout, so anything mounted below
 * that is torn down on each in-workspace tab click. Holding the state here lets
 * an answer keep streaming — and keep persisting — while the user navigates or
 * closes the drawer.
 */
/**
 * The `context` half of a question's payload: the ids of whatever the user
 * attached to it, or nothing at all.
 *
 * Omitted entirely when nothing is attached, so an unattached question asks
 * over the whole package as it always has, rather than over an empty list.
 *
 * Both id lists are sent whenever it is present, one of them empty: only one
 * kind can be attached at a time, but the shape the server documents carries
 * both keys.
 */
const buildContext = ({ documents, findings }: AddContextSelection) => {
  if (documents.length === 0 && findings.length === 0) return {};

  return {
    context: {
      documentIds: documents.map((document) => document.id),
      findingIds: findings.map((finding) => finding.id),
    },
  };
};

export const AgentChatSessionProvider: FC<PropsWithChildren<AgentChatSessionProviderProps>> = ({
  children,
  packageId,
  projectId,
}) => {
  const { t } = useTranslation();
  const [session, setSession] = useState<AgentChatSession>(() => readStoredSession(packageId));
  const [seededPackageId, setSeededPackageId] = useState(packageId);
  const sessionRef = useRef(session);
  const packageIdRef = useRef(packageId);
  const abortControllerRef = useRef<AbortController | null>(null);
  const chatApiBaseUrl = import.meta.env.VITE_CHAT_API_BASE_URL;
  const { attachments } = useAgentChatAttachments();
  const attachmentsRef = useRef(attachments);
  const isContextEnabled = useFeatureFlag(FeatureFlag.PropChatContext);
  const isContextEnabledRef = useRef(isContextEnabled);

  // Moving to another package swaps in that package's stored history. Adjusting
  // state during render is the supported way to do that, so the swap is visible
  // in the same paint — but only setState is safe here, never a ref write.
  if (seededPackageId !== packageId) {
    setSeededPackageId(packageId);
    setSession(readStoredSession(packageId));
  }

  // The refs the streaming writer reads are re-seeded after the commit lands, so
  // a render that gets thrown away cannot leave them pointing at a package that
  // never mounted — which would make every later commit fail its own guard and
  // silently freeze the answer mid-sentence.
  useEffect(() => {
    if (packageIdRef.current === packageId) return;

    packageIdRef.current = packageId;
    sessionRef.current = readStoredSession(packageId);
  }, [packageId]);

  /*
   * Read at send time rather than closed over: sendMessage is memoised on the
   * package, so a copy captured before the user attached anything would keep
   * sending the ids they had then - none of them.
   */
  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Read at send time for the same reason, and because Unleash can flip it
  // between the memoised sendMessage being built and a question being asked.
  useEffect(() => {
    isContextEnabledRef.current = isContextEnabled;
  }, [isContextEnabled]);

  // Leaving the package (or tearing the app down) stops the stream instead of
  // letting it read to completion against a conversation nobody is watching.
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    },
    [packageId],
  );

  const sendMessage = useCallback(
    async (message: Message, language: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      const { signal } = controller;
      const targetPackageId = packageId;
      const token = getSessionFromStorage();
      const { isResumedChat } = sessionRef.current;

      // Minted on the first question rather than when the drawer opens, so a
      // package the user never chats to writes nothing. Captured here for the
      // same reason streamMessages is: read through the ref later, a package
      // switch mid-answer could persist the newly loaded package's id under
      // this package's key.
      const conversationId = sessionRef.current.conversationId ?? crypto.randomUUID();

      // The stream owns its copy of the conversation. Reading the shared
      // session ref instead would let a package switch mid-answer persist the
      // newly loaded package's messages under this package's key.
      let streamMessages = sessionRef.current.messages;

      // Persisting happens here rather than inside a setState updater. React
      // skips updater functions once a component stops accepting updates, which
      // previously dropped the write and froze the answer mid-sentence.
      // `persist` is false for the per-character animation frames: those need a
      // render, not a storage write. Serialising the whole conversation on every
      // character is what made streaming expensive; the chunk boundary and the
      // finally below still persist, so nothing is lost.
      const commit = (messages: Message[], isStreaming: boolean, persist = true) => {
        streamMessages = messages;
        if (persist) storeSession(targetPackageId, conversationId, messages);

        // Only the package still on screen may drive what is rendered.
        if (signal.aborted || packageIdRef.current !== targetPackageId) return;

        const next = { conversationId, isResumedChat, isStreaming, messages };
        sessionRef.current = next;
        setSession(next);
      };

      commit([...streamMessages, message], true);

      const connectController = new AbortController();
      const connectTimer = setTimeout(
        () => connectController.abort(new QueryError('SERVICE_UNAVAILABLE')),
        CONNECT_TIMEOUT_MS,
      );

      try {
        const response = await fetch(
          `${chatApiBaseUrl}/projects/${projectId}/packages/${targetPackageId}/query`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token?.idToken.token ? `Bearer ${token.idToken.token}` : '',
            },
            // mapToSnakeCase rather than a hand-written conversation_id: the
            // key has to be snake_case on the wire, and spelling it out here
            // invites a reviewer to "tidy" it back to camelCase — which the
            // server would silently ignore, costing the agent its memory
            // without any error to notice.
            body: JSON.stringify(
              mapToSnakeCase({
                question: message.body,
                language,
                conversationId,
                /*
                 * Nothing can be attached while context is off, but the store
                 * lives above the Outlet and outlives the drawer - so a flag
                 * flipped off mid-session would otherwise keep putting a stale
                 * context on the wire with no UI left to clear it from.
                 */
                ...(isContextEnabledRef.current ? buildContext(attachmentsRef.current) : {}),
              }),
            ),
            signal: AbortSignal.any([signal, connectController.signal]),
          },
        );
        clearTimeout(connectTimer);

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw new QueryError(typeof body?.code === 'string' ? body.code : undefined);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('Streaming not supported');

        const decoder = new TextDecoder();
        let accumulatedText = '';
        let sources: MessageSource[] = [];
        let steps: MessageStep[] = [];
        let stepsStartedAt: number | null = null;
        let thinkingSeconds: number | undefined;
        let buffer = '';
        const aiMessageId = crypto.randomUUID();

        commit(
          [
            ...streamMessages,
            {
              id: aiMessageId,
              authorId: 'ai',
              body: '',
              sources: [],
              steps: [],
              createdAt: new Date().toISOString(),
            },
          ],
          true,
        );

        const commitAnswer = (persist: boolean) =>
          commit(
            streamMessages.map((storedMessage) =>
              storedMessage.id === aiMessageId
                ? { ...storedMessage, body: accumulatedText, sources, steps, thinkingSeconds }
                : storedMessage,
            ),
            true,
            persist,
          );

        // Reading and revealing run independently. The reader never waits on
        // the animation, so a slow reveal cannot throttle the download, and the
        // gap between them is what lets the drain speed up: once the stream is
        // finished there is nothing left to wait for, so the remaining text is
        // revealed at DRAIN_SPEED_MULTIPLIER.
        let pending = '';
        let isStreamComplete = false;

        const persistReceived = () =>
          storeSession(
            targetPackageId,
            conversationId,
            streamMessages.map((storedMessage) =>
              storedMessage.id === aiMessageId
                ? {
                    ...storedMessage,
                    body: accumulatedText + pending,
                    sources,
                    steps,
                    thinkingSeconds,
                  }
                : storedMessage,
            ),
          );

        const readStream = async () => {
          try {
            while (!signal.aborted) {
              const { value, done } = await reader.read();
              if (done) break;

              buffer += decoder.decode(value, { stream: true });

              try {
                const jsonObjects = buffer
                  .split('\n')
                  .filter((line) => line.trim() !== '')
                  .map((line) => JSON.parse(line));

                buffer = '';

                let hasNewText = false;
                let hasNewStep = false;

                for (const jsonChunk of jsonObjects) {
                  if (jsonChunk.error) {
                    throw new QueryError(jsonChunk.error.code);
                  }

                  if (jsonChunk.sources) {
                    // Either shape the stream sends them in, collapsed to the
                    // documents the source list can look up - see
                    // parseStreamSources. Sent once, but not necessarily as the
                    // first line any more: step events can precede it.
                    sources = parseStreamSources(jsonChunk.sources);
                    continue;
                  }

                  if (jsonChunk.step) {
                    stepsStartedAt ??= Date.now();
                    const incoming = jsonChunk.step as MessageStep;
                    const index = steps.findIndex((step) => step.key === incoming.key);
                    steps =
                      index === -1
                        ? [...steps, incoming]
                        : steps.map((step, i) => (i === index ? incoming : step));
                    hasNewStep = true;
                    continue;
                  }

                  if (jsonChunk.answer) {
                    // The answer starting is itself the signal that the
                    // preamble is over - steps still active are closed out
                    // and the elapsed time is frozen here, once, rather than
                    // relying on the backend to fabricate a closing event for
                    // paths (e.g. a deadline fallback) that never naturally
                    // produce one. Frozen rather than live-recomputed, so a
                    // later reload replays the same numbers.
                    if (stepsStartedAt !== null && thinkingSeconds === undefined) {
                      thinkingSeconds = Math.max(
                        1,
                        Math.round((Date.now() - stepsStartedAt) / 1000),
                      );
                      steps = steps.map((step) =>
                        step.status === 'active' ? { ...step, status: 'done' } : step,
                      );
                      hasNewStep = true;
                    }
                    pending += String(jsonChunk.answer);
                    hasNewText = true;
                  }
                }

                // Steps must render as they arrive, not wait for the
                // character-reveal loop below - that loop only commits once
                // there is answer text to reveal.
                if (hasNewStep) commitAnswer(false);

                // Persist what has been received, not what has been revealed:
                // a reload should recover the whole answer, not the frame the
                // animation happened to be on. Once per network chunk, not per
                // frame.
                if (hasNewText) persistReceived();
              } catch (error) {
                if (error instanceof QueryError) throw error;
                console.log('Error processing JSON chunk:', error);
              }
            }
          } finally {
            isStreamComplete = true;
          }
        };

        const revealAnswer = async () => {
          while (!signal.aborted) {
            if (pending.length === 0) {
              if (isStreamComplete) break;
              await nextFrame();
              continue;
            }

            const perFrame =
              charactersPerFrame(accumulatedText.length) *
              (isStreamComplete ? DRAIN_SPEED_MULTIPLIER : 1);
            const take = Math.min(pending.length, perFrame);

            accumulatedText += pending.slice(0, take);
            pending = pending.slice(take);
            commitAnswer(false);
            await nextFrame();
          }
        };

        await Promise.all([readStream(), revealAnswer()]);

        // Anything received but not revealed — an abort mid-reveal — is still
        // the answer, so fold it in before the closing commit persists it.
        accumulatedText += pending;
        pending = '';
        commitAnswer(false);

        if (signal.aborted) await reader.cancel().catch(() => undefined);
      } catch (error) {
        if (!signal.aborted) {
          console.error('Error fetching response:', error);
          const failureKey = QUERY_FAILURE_KEYS[(error as QueryError)?.code ?? ''];
          commit(
            [
              ...streamMessages,
              {
                id: crypto.randomUUID(),
                body: t(failureKey ?? 'prop-chat.query-failed'),
                authorId: crypto.randomUUID(),
                createdAt: new Date().toISOString(),
              },
            ],
            true,
          );
        }
      } finally {
        clearTimeout(connectTimer);
        // An answer that never produced a character leaves an empty agent
        // message behind, which would be persisted and come back as a blank
        // bubble — and, being empty, would render a second thinking skeleton
        // alongside the next question's. Filtered rather than trimmed from the
        // end, because the failure path appends its message after this one.
        const withoutEmptyAnswer = streamMessages.filter(
          (storedMessage) => storedMessage.authorId !== 'ai' || storedMessage.body !== '',
        );

        commit(withoutEmptyAnswer, false);
        if (abortControllerRef.current === controller) abortControllerRef.current = null;
      }
    },
    [chatApiBaseUrl, packageId, projectId, t],
  );

  const value = useMemo(() => ({ sendMessage, session }), [sendMessage, session]);

  return (
    <AgentChatSessionContext.Provider value={value}>{children}</AgentChatSessionContext.Provider>
  );
};

export const useAgentChatSession = () => {
  const context = useContext(AgentChatSessionContext);

  if (!context) {
    throw new Error('Missing AgentChatSessionProvider in tree');
  }

  return context;
};
